import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

const LANGUAGE_KEY = "app_language";
const DEFAULT_LANGUAGE = "en";

interface LanguageContextType {
  isGerman: boolean;
  isEng: boolean;
  language: "en" | "ger";
  setLanguage: (lang: "en" | "ger") => void;
  toggleLanguage: () => void;
  currentLanguage: "en" | "ger";
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<"en" | "ger">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(LANGUAGE_KEY);
      return (saved === "ger" ? "ger" : "en") as "en" | "ger";
    }
    return DEFAULT_LANGUAGE as "en" | "ger";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(LANGUAGE_KEY, language);
    }
  }, [language]);

  const setLanguage = (lang: "en" | "ger") => {
    setLanguageState(lang);
  };

  const toggleLanguage = () => {
    setLanguageState((prev) => (prev === "en" ? "ger" : "en"));
  };

  const value: LanguageContextType = {
    isGerman: language === "ger",
    isEng: language === "en",
    language,
    setLanguage,
    toggleLanguage,
    currentLanguage: language,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
